import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import apiService from '../services/ApiService';

const Vehicles = () => {
  const [users, setUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const userId = params.get('userId');
    if (userId) {
      setSelectedUserId(userId);
    }
  }, [location]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [otpStep, setOtpStep] = useState(1);
  const [otp, setOtp] = useState('');
  const [isOtpLoading, setIsOtpLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [viewingDoc, setViewingDoc] = useState(null);

  const DOWNLOAD_PHONE = '9554494415';
  const itemsPerPage = 10;

  const fetchUsers = React.useCallback(async () => {
    setLoadingUsers(true);
    try {
      const result = await apiService.get('/users');
      setUsers(result.users || []);
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const fetchVehicles = React.useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.get(`/vehicles/admin/user/${selectedUserId}`);
      setVehicles(result.vehicles || []);
    } catch (err) {
      console.error('Failed to fetch vehicles:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedUserId]);

  useEffect(() => {
    if (selectedUserId) {
      fetchVehicles();
    } else {
      setVehicles([]);
    }
  }, [selectedUserId, fetchVehicles]);

  const filteredVehicles = useMemo(() => {
    return vehicles.filter(v => {
      const searchableText = [
        v.vehicle_number,
        v.brand,
        v.model,
        v.vehicle_type,
        v.fuel_type
      ].join(' ').toLowerCase();
      return searchableText.includes(searchQuery.toLowerCase());
    });
  }, [vehicles, searchQuery]);

  const paginatedVehicles = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredVehicles.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredVehicles, currentPage]);

  const totalPages = Math.ceil(filteredVehicles.length / itemsPerPage);

  const handleRequestOtp = async () => {
    setIsOtpLoading(true);
    try {
      await apiService.post('/users/select-type', {
        user_type: 'admin',
        phone_number: DOWNLOAD_PHONE
      });
      setOtpStep(2);
      alert('OTP sent to ' + DOWNLOAD_PHONE);
    } catch (err) {
      alert(err.message || 'Failed to send OTP');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const handleVerifyOtpAndDownload = async () => {
    if (otp.length !== 6) return alert('Enter 6-digit OTP');
    setIsOtpLoading(true);
    try {
      await apiService.post('/users/verify-otp', {
        phone_number: DOWNLOAD_PHONE,
        otp
      });
      setShowOtpModal(false);
      await performDownload();
    } catch (err) {
      alert(err.message || 'Invalid OTP');
    } finally {
      setIsOtpLoading(false);
    }
  };

  const performDownload = async () => {
    setIsDownloading(true);
    try {
      const result = await apiService.get(`/vehicles/admin/user/${selectedUserId}?limit=10000`);
      const allVehicles = result.vehicles || [];

      const headers = ['Vehicle Number', 'Brand', 'Model', 'Type', 'Year', 'Fuel', 'Insurance'];
      const csvContent = [
        headers.join(','),
        ...allVehicles.map(v => [
          v.vehicle_number,
          v.brand,
          v.model,
          v.vehicle_type,
          v.year_of_registration,
          v.fuel_type,
          v.insurance_status ? 'Active' : 'Expired'
        ].map(f => `"${f}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `vehicles_${selectedUserId}_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      alert('Download failed');
    } finally {
      setIsDownloading(false);
    }
  };

  if (loadingUsers) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: '100px' }}>
      <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: 'var(--primary)' }}></i>
    </div>
  );

  return (
    <div className="vehicles-page">
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '32px', gap: '20px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ fontFamily: 'Outfit', fontSize: '24px', fontWeight: '800', margin: '0', color: 'var(--text-main)' }}>Vehicles Repository</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>Analyze and manage all registered vehicle documentation.</p>
        </div>
        {selectedUserId && (
          <button className="btn btn-primary" onClick={() => setShowOtpModal(true)} disabled={isDownloading}>
            {isDownloading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-file-download"></i>}
            <span>Export Registry</span>
          </button>
        )}
      </div>

      <div className="admin-card" style={{ marginBottom: '32px', padding: '24px' }}>
        <div className="grid-responsive">
          <div className="col-4 col-lg-12">
            <label style={labelStyle}>SELECT OWNER</label>
            <div style={{ position: 'relative' }}>
              <i className="fas fa-user-circle" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--primary)', zIndex: 1 }}></i>
              <select
                value={selectedUserId}
                onChange={e => setSelectedUserId(e.target.value)}
                style={{ ...inputStyle, paddingLeft: '40px', background: 'white' }}
              >
                <option value="">Search or select a user...</option>
                {users.map(u => (
                  <option key={u.user_id} value={u.user_id}>{u.name || u.phone_number}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="col-8 col-lg-12">
            <label style={labelStyle}>QUICK FIND</label>
            <div style={{ position: 'relative' }}>
              <i className="fas fa-search" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }}></i>
              <input
                type="text"
                placeholder="Enter vehicle number, brand or model to filter..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                disabled={!selectedUserId}
                style={{ ...inputStyle, paddingLeft: '40px', background: 'white' }}
              />
            </div>
          </div>
        </div>
      </div>

      {!selectedUserId ? (
        <div className="admin-card" style={{ textAlign: 'center', padding: '80px 40px', border: '2px dashed var(--border-light)', background: 'transparent' }}>
          <div style={{ width: '80px', height: '80px', background: 'var(--primary-light)', color: 'var(--primary)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: '32px' }}>
            <i className="fas fa-car-side"></i>
          </div>
          <h3 style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-main)', margin: '0 0 8px' }}>Asset Search Required</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', maxWidth: '400px', margin: '0 auto' }}>
            Please select a vehicle owner from the dropdown above to access their vehicle records and compliance documents.
          </p>
        </div>
      ) : loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
          <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '24px', color: 'var(--primary)' }}></i>
        </div>
      ) : (
        <div className="admin-card" style={{ padding: '0', overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: 'var(--bg-main)', textAlign: 'left' }}>
                  <th style={tableHeaderStyle}>ASSET DETAILS</th>
                  <th style={tableHeaderStyle}>LICENSE PLATE</th>
                  <th style={{ ...tableHeaderStyle }} className="hide-mobile">COMPLIANCE</th>
                  <th style={{ ...tableHeaderStyle }} className="hide-mobile">DOCUMENTS</th>
                  <th style={tableHeaderStyle}>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {paginatedVehicles.length > 0 ? paginatedVehicles.map(v => (
                  <tr key={v._id} style={{
                    borderBottom: '1px solid var(--border-light)',
                    transition: 'background 0.2s'
                  }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = 'var(--primary-light)'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td style={tableCellStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '42px', height: '42px', background: 'var(--bg-main)', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)' }}>
                          <i className={`fas fa-${v.vehicle_type === 'Commercial' ? 'truck' : 'car'}`}></i>
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', color: 'var(--text-main)' }}>{v.brand} {v.model}</div>
                          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{v.fuel_type} • {v.year_of_registration}</div>
                        </div>
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <span style={{
                        fontFamily: 'Inter', fontWeight: '800', fontSize: '13px',
                        background: '#1e293b', color: 'white',
                        padding: '6px 12px', borderRadius: '6px',
                        letterSpacing: '1px'
                      }}>
                        {v.vehicle_number.toUpperCase()}
                      </span>
                    </td>
                    <td style={tableCellStyle} className="hide-mobile">
                      <span className={`status-badge status-${v.insurance_status ? 'completed' : 'rejected'}`} style={{ fontSize: '11px' }}>
                        {v.insurance_status ? 'INSURED' : 'UNINSURED'}
                      </span>
                    </td>
                    <td style={tableCellStyle} className="hide-mobile">
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--text-muted)', fontSize: '13px' }}>
                        <i className="far fa-file-pdf"></i>
                        <span style={{ fontWeight: '600' }}>{v.documents?.length || 0} Files</span>
                      </div>
                    </td>
                    <td style={tableCellStyle}>
                      <button className="btn btn-outline" style={{ padding: '8px 16px', fontSize: '13px' }} onClick={() => setSelectedVehicle(v)}>
                        <i className="fas fa-eye" style={{ marginRight: '8px' }}></i> View Details
                      </button>
                    </td>
                  </tr>
                )) : (
                  <tr>
                    <td colSpan="5" style={{ padding: '48px', textAlign: 'center', color: 'var(--text-dim)' }}>
                      No vehicles found for the selected criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div style={{ padding: '24px', borderTop: '1px solid var(--border-light)', display: 'flex', justifyContent: 'center', gap: '12px', background: 'var(--bg-main)' }}>
              <button className="btn btn-outline" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                <i className="fas fa-chevron-left" style={{ fontSize: '10px' }}></i>
              </button>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '700' }}>
                <span style={{ color: 'var(--primary)' }}>{currentPage}</span>
                <span style={{ color: 'var(--text-dim)' }}>/ {totalPages}</span>
              </div>
              <button className="btn btn-outline" disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                <i className="fas fa-chevron-right" style={{ fontSize: '10px' }}></i>
              </button>
            </div>
          )}
        </div>
      )}

      {/* Detail Modal */}
      {selectedVehicle && (
        <div className="modal-overlay" onClick={() => setSelectedVehicle(null)}>
          <div className="admin-card" style={{ maxWidth: '800px', width: '95%', padding: '0', overflow: 'hidden', border: 'none' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '32px', background: 'var(--grad-primary)', color: 'white', position: 'relative', overflow: 'hidden' }}>
              <i className="fas fa-car" style={{ position: 'absolute', right: '-20px', bottom: '-20px', fontSize: '120px', opacity: 0.1 }}></i>
              <div style={{ position: 'relative', zIndex: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '800', letterSpacing: '2px', opacity: 0.8, textTransform: 'uppercase', marginBottom: '8px' }}>Vehicle Profile</div>
                  <h3 style={{ margin: 0, fontSize: '28px', fontWeight: '800' }}>{selectedVehicle.brand} {selectedVehicle.model}</h3>
                  <div style={{ marginTop: '12px', display: 'flex', gap: '12px' }}>
                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>{selectedVehicle.vehicle_number}</span>
                    <span style={{ background: 'rgba(255,255,255,0.2)', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>{selectedVehicle.fuel_type}</span>
                  </div>
                </div>
                <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setSelectedVehicle(null)}>×</button>
              </div>
            </div>

            <div style={{ padding: '32px' }} className="grid-responsive">
              <div className="col-6 col-md-12">
                <h4 style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-info-circle" style={{ color: 'var(--primary)' }}></i> Technical Specs
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <DetailItem label="Owner Name" value={selectedVehicle.owner_name || 'N/A'} />
                  <DetailItem label="Engine Number" value={selectedVehicle.engine_number || 'N/A'} />
                  <DetailItem label="Norms Type" value={selectedVehicle.norms_type || 'N/A'} />
                  <DetailItem label="Registration Year" value={selectedVehicle.year_of_registration} />
                  <DetailItem label="Vehicle Type" value={selectedVehicle.vehicle_type} />
                  <DetailItem label="Fuel Category" value={selectedVehicle.fuel_type} />
                  <DetailItem label="Chassis Number" value={selectedVehicle.chassis_number || 'N/A'} />
                </div>
              </div>

              <div className="col-6 col-md-12">
                <h4 style={{ fontSize: '14px', fontWeight: '800', color: 'var(--text-main)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <i className="fas fa-copy" style={{ color: 'var(--primary)' }}></i> Compliance Docs
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {selectedVehicle.documents?.length > 0 ? selectedVehicle.documents.map((doc, idx) => {
                    const isPdf = doc.file_path.toLowerCase().endsWith('.pdf');
                    return (
                    <div key={idx} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '16px', background: 'var(--bg-main)', border: '1px solid var(--border-light)', borderRadius: '12px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {isPdf ? (
                          <div style={{ width: '48px', height: '48px', background: 'var(--error-light)', color: 'var(--error)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <i className="fas fa-file-pdf"></i>
                          </div>
                        ) : (
                          <img src={doc.file_path} alt={doc.document_type} style={{ width: '48px', height: '48px', objectFit: 'cover', borderRadius: '8px', border: '1px solid var(--border-light)' }} />
                        )}
                        <div>
                          <div style={{ fontWeight: '700', fontSize: '14px' }}>{doc.document_type}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>Exp: {doc.expiry_date ? new Date(doc.expiry_date).toLocaleDateString('en-GB') : 'Lifetime'}</div>
                        </div>
                      </div>
                      <button onClick={() => setViewingDoc(doc)} className="btn btn-outline" style={{ padding: '6px 12px', fontSize: '11px', cursor: 'pointer' }}>
                        <i className={isPdf ? "fas fa-external-link-alt" : "fas fa-eye"} style={{ marginRight: '6px' }}></i> View
                      </button>
                    </div>
                    );
                  }) : (
                    <div style={{ textAlign: 'center', padding: '24px', background: 'var(--bg-main)', borderRadius: '12px', color: 'var(--text-dim)', fontSize: '13px' }}>
                      No digital assets attached to this vehicle portfolio.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* OTP Download Modal */}
      {showOtpModal && (
        <div className="modal-overlay">
          <div className="admin-card" style={{ maxWidth: '420px', width: '90%', padding: '0', overflow: 'hidden', border: 'none' }}>
            <div style={{ padding: '24px', background: 'var(--grad-primary)', color: 'white', textAlign: 'center' }}>
              <div style={{ width: '56px', height: '56px', background: 'rgba(255,255,255,0.2)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: '24px' }}>
                <i className="fas fa-shield-alt"></i>
              </div>
              <h3 style={{ margin: 0, fontSize: '20px', fontWeight: '800' }}>Admin Verification</h3>
              <p style={{ margin: '8px 0 0', opacity: 0.8, fontSize: '12px' }}>Sensitive data export requires multi-factor authentication.</p>
            </div>

            <div style={{ padding: '32px' }}>
              {otpStep === 1 ? (
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-muted)', marginBottom: '24px' }}>
                    A one-time password will be sent to the primary admin contact: <br /><strong style={{ color: 'var(--text-main)' }}>{DOWNLOAD_PHONE}</strong>
                  </p>
                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleRequestOtp} disabled={isOtpLoading}>
                    {isOtpLoading ? <i className="fas fa-spinner fa-spin"></i> : 'Request Security Code'}
                  </button>
                  <button className="btn btn-outline" style={{ width: '100%', marginTop: '12px' }} onClick={() => setShowOtpModal(false)}>Discard Request</button>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <div className="form-group" style={{ marginBottom: '24px' }}>
                    <label style={{ ...labelStyle, textAlign: 'center' }}>ENTER 6-DIGIT CORE</label>
                    <input
                      type="text"
                      className="form-control"
                      maxLength="6"
                      value={otp}
                      onChange={e => setOtp(e.target.value.replace(/\D/g, ''))}
                      style={{
                        textAlign: 'center', fontSize: '28px', letterSpacing: '8px',
                        fontWeight: '800', background: 'var(--bg-main)', border: 'none',
                        borderRadius: '12px', padding: '16px'
                      }}
                      placeholder="••••••"
                    />
                  </div>
                  <button className="btn btn-primary" style={{ width: '100%' }} onClick={handleVerifyOtpAndDownload} disabled={isOtpLoading}>
                    {isOtpLoading ? <i className="fas fa-sync fa-spin"></i> : 'Authorize & Export'}
                  </button>
                  <button className="btn btn-outline" style={{ width: '100%', marginTop: '12px' }} onClick={() => setOtpStep(1)}>Re-send Code</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Document Viewer Modal */}
      {viewingDoc && (
        <div className="modal-overlay" style={{ zIndex: 3000 }} onClick={() => setViewingDoc(null)}>
          <div className="admin-card" style={{ maxWidth: '900px', width: '95%', height: '85vh', padding: '0', display: 'flex', flexDirection: 'column', overflow: 'hidden', border: 'none' }} onClick={e => e.stopPropagation()}>
            <div style={{ padding: '20px 24px', background: 'var(--grad-primary)', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ width: '36px', height: '36px', background: 'rgba(255,255,255,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <i className={viewingDoc.file_path.toLowerCase().endsWith('.pdf') ? "fas fa-file-pdf" : "fas fa-image"}></i>
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800' }}>{viewingDoc.document_type}</h3>
                  <div style={{ fontSize: '11px', opacity: 0.8 }}>Previewing Document</div>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '12px' }}>
                <a href={viewingDoc.file_path} target="_blank" rel="noreferrer" style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '14px', cursor: 'pointer', padding: '8px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none', fontWeight: '600' }}>
                  <i className="fas fa-external-link-alt"></i> Open
                </a>
                <button style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', fontSize: '20px', cursor: 'pointer', width: '36px', height: '36px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }} onClick={() => setViewingDoc(null)}>×</button>
              </div>
            </div>
            
            <div style={{ flex: 1, padding: '24px', background: 'var(--bg-main)', overflowY: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              {viewingDoc.file_path.toLowerCase().endsWith('.pdf') ? (
                <iframe src={viewingDoc.file_path} style={{ width: '100%', height: '100%', border: 'none', borderRadius: '8px', boxShadow: 'var(--shadow-sm)' }} title={viewingDoc.document_type} />
              ) : (
                <img src={viewingDoc.file_path} alt={viewingDoc.document_type} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: '8px', boxShadow: 'var(--shadow-sm)' }} />
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        .modal-overlay { position: fixed; inset: 0; background: rgba(15, 23, 42, 0.7); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 2000; padding: 20px; }
      `}</style>
    </div>
  );
};

const DetailItem = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
    <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--text-dim)', textTransform: 'uppercase' }}>{label}</span>
    <span style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-main)' }}>{value}</span>
  </div>
);

const tableHeaderStyle = {
  padding: '16px 24px',
  color: 'var(--text-muted)',
  fontSize: '11px',
  fontWeight: '800',
  letterSpacing: '1px',
  borderBottom: '1px solid var(--border-light)'
};

const tableCellStyle = {
  padding: '20px 24px',
  fontSize: '14px',
  color: 'var(--text-main)'
};

const labelStyle = {
  display: 'block',
  marginBottom: '8px',
  fontWeight: '600',
  fontSize: '13px',
  color: 'var(--text-dim)',
  letterSpacing: '0.5px'
};

const inputStyle = {
  width: '100%',
  padding: '12px',
  borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-light)',
  fontSize: '14px',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'all 0.2s'
};

export default Vehicles;